import { useQuery } from '@tanstack/react-query';

import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CountryLabel } from '@/components/shared/CountryLabel';
import { DetailList, type DetailItem } from '@/components/shared/DetailList';
import { StatusPill } from '@/components/shared/StatusPill';
import { getMyTenant, TENANT_ME_QUERY_KEY, type MyTenant } from '@/lib/api/tenants';
import { formatDate, orDash } from '@/lib/format';

/**
 * Settings — cross-cutting account configuration (apps/web CLAUDE.md
 * §10.1.7). Store and carrier connections moved to their own
 * `/app/stores` and `/app/carriers` sections; this page keeps the
 * org-level cards: company details and billing. Read-only in MVP, off
 * the `tenant/me` query the `/app` loader already warms.
 */
export function SettingsPage() {
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });

  const tenant = tenantQuery.data;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Your account configuration" />
      {tenant ? (
        <div className="space-y-6">
          <CompanyCard tenant={tenant} />
          <BillingCard tenant={tenant} />
        </div>
      ) : null}
    </div>
  );
}

function CompanyCard({ tenant }: { tenant: MyTenant }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Company</CardTitle>
        <CardDescription>Used on your customs declarations, invoices, and labels.</CardDescription>
      </CardHeader>
      <CardContent>
        <DetailList
          items={[
            { label: 'Legal name', value: orDash(tenant.legal_name) },
            { label: 'Trading name', value: orDash(tenant.trading_name) },
            { label: 'Country', value: <CountryLabel code={tenant.country} /> },
            {
              label: 'Currency',
              value: <span className="tabular-nums">{orDash(tenant.currency)}</span>,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function BillingCard({ tenant }: { tenant: MyTenant }) {
  const status = tenant.billing_status;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <CardDescription>Per-label pricing, billed monthly. No tiers, no minimums.</CardDescription>
        {status ? <CardAction>{renderBillingStatus(status)}</CardAction> : null}
      </CardHeader>
      <CardContent>
        {status ? (
          <DetailList items={billingItems(tenant, status)} />
        ) : (
          <p className="text-muted-foreground text-sm">Billing isn&apos;t set up yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

function billingItems(
  tenant: MyTenant,
  status: NonNullable<MyTenant['billing_status']>,
): DetailItem[] {
  const items: DetailItem[] = [
    {
      label: 'Pricing',
      value: (
        <>
          <span className="tabular-nums">£0.03</span> per shipment
        </>
      ),
    },
  ];
  if (status === 'trial' && tenant.trial_ends_at) {
    items.push({ label: 'Trial ends', value: formatDate(tenant.trial_ends_at) });
  }
  items.push({ label: 'Payment', value: 'Card on file' });
  return items;
}

function renderBillingStatus(status: NonNullable<MyTenant['billing_status']>) {
  switch (status) {
    case 'active':
      return <StatusPill tone="success">Active</StatusPill>;
    case 'trial':
      return <StatusPill tone="warning">Trial</StatusPill>;
    case 'past_due':
      return <StatusPill tone="warning">Past due</StatusPill>;
    case 'cancelled':
      return <StatusPill tone="muted">Cancelled</StatusPill>;
    case 'unbilled':
      return <StatusPill tone="muted">Unbilled</StatusPill>;
  }
}
